import { createContext, useContext, useState, useEffect, useRef } from "react";

import LoadingAnimation from "../Components/Loading/LoadingAnimation";
import { GETALLUSERSBYID_API } from "../Constants/apiRoutes";
import { useNavigate } from "react-router-dom";
import api from "../Components/ProtectedRoute/axiosInstance"; 
const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [permissionsID, setPermissionsID] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);

  const navigate = useNavigate();

  const IDLE_TIMEOUT = 30 * 60 * 1000; // 30 minutes

  const idleTimerRef = useRef(null);

  useEffect(() => {
    const checkAuthStatus = async () => {
      const pathname = window.location.pathname;

      const isPublicAccessRoute = /^\/public-access\/[^/]+\/[^/]+$/.test(pathname);

      if (isPublicAccessRoute) {
        setLoading(false);
        return;
      }

      const token = localStorage.getItem("token");
      const userID = localStorage.getItem("UserID");
      const tenantID = localStorage.getItem("TenantID");
      const roleID = localStorage.getItem("RoleID");
      const permissionIDs = localStorage.getItem("PermissionIDs");

      if (!token || !userID || !tenantID) {
        logout();
        navigate("/");
        setLoading(false);
        return;
      }

      try {
        await fetchUserDetails(token, userID);
        setIsLoggedIn(true);
        setUserRole(roleID);
        setPermissionsID(permissionIDs);
      } catch (error) {
        logout();
        navigate("/");
      } finally {
        setLoading(false);
      }
    };

    checkAuthStatus();
  }, []);

  useEffect(() => {
    const events = ["mousemove", "keydown", "mousedown", "scroll", "touchstart"];

    const resetIdleTimer = () => {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => {
        handleLogout();
      }, IDLE_TIMEOUT);
    };

    if (isLoggedIn) {
      events.forEach((event) => window.addEventListener(event, resetIdleTimer));
      resetIdleTimer();
    }

    return () => {
      clearTimeout(idleTimerRef.current);
      events.forEach((event) => window.removeEventListener(event, resetIdleTimer));
    };
  }, [isLoggedIn]);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const login = async (token, roleID, userID, departmentIDs, permissionIDs) => {
    localStorage.setItem("token", token);
    localStorage.setItem("UserID", userID);
    localStorage.setItem("TenantID", localStorage.getItem("TenantID"));
    localStorage.setItem("RoleID", roleID);
    localStorage.setItem("DepartmentIDs", departmentIDs);
    localStorage.setItem("PermissionID", JSON.stringify(permissionIDs || []));

    setIsLoggedIn(true);
    setUserRole(roleID);
    setPermissionsID(permissionIDs || []);

    await fetchUserDetails(token, userID);
  };

  const fetchUserDetails = async (token, userID) => {
    try {
      const response = await  api.get(`${GETALLUSERSBYID_API}/${userID}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const userDataResponse = response.data;
      const userDetails = userDataResponse?.user || null;

      if (userDetails) {
        localStorage.setItem("userData", JSON.stringify(userDetails));
        setUserData(userDetails);
      }
    } catch (error) {
      console.error("Error fetching user details:", error);
      setUserData(null);
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("PermissionIDs");
    localStorage.removeItem("userData");
    localStorage.removeItem("DepartmentIDs");
    localStorage.removeItem("TenantID");
    localStorage.removeItem("RoleID");
    localStorage.removeItem("UserID");
    setIsLoggedIn(false);
    setUserRole(null);
    setPermissionsID([]);
  };

  if (loading) {
    return <LoadingAnimation />;
  }

  return (
    <AuthContext.Provider
      value={{
        isLoggedIn,
        userRole,
        permissionsID,
        login,
        logout,
        loading,
        userData,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
